import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import PageHeader from "../../../components/pageHeader/pageHeader";
import {
  Breadcrumbs,
  Container,
  Link,
  Button,
  Box,
  CircularProgress,
} from "@material-ui/core";
import {
  getPowerBIEmbed,
  exportPowerBIReport,
  getPowerBIExportStatus,
} from "../../../common/services/powerbi.service";
import NetworkService from "../../../common/services/networkService";
import { APP_BASE_URL_DEV } from "../../../common/configs/auth.config";

// ========================================
// POWER BI REPORT ID CONFIGURATION
// ========================================
const REPORT_CONFIG = {
  calf: {
    id: "ec1115b6-f8b6-440b-8f0f-af1edebd5b1a",
    label: "Calf Report",
    scope: undefined,
  },
  custom: {
    id: "1752a5c5-9f05-4486-85c8-34371d9cb6e7",
    label: "Custom Reporting",
    scope: undefined,
  },
  historical: {
    id: "c2e39f5e-633a-4461-a4ed-0cdd8247a03e",
    label: "Historical Report",
    scope: undefined,
  },
  monthlyhealth: {
    id: "78b9c37b-80fc-41b7-bc5e-70cf83bc668e",
    label: "Monthly Health Events",
    scope: undefined,
  },
};
// ========================================

const PowerBi = () => {
  const { reportType } = useParams();
  const [powerBIParams, setPowerBIParams] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Get report config based on route parameter, default to calf report
  const currentReport = REPORT_CONFIG[reportType] || REPORT_CONFIG.calf;

  useEffect(
    function effectFunction() {
      // Fetch Power BI embed configuration from API
      setPowerBIParams(null); // Clear existing report when reportType changes

      getPowerBIEmbed(currentReport.id, currentReport.scope)
        .then((response) => {
          console.log("Power BI API Response:", response.data);
          setPowerBIParams({
            type: "report",
            embedUrl: response.data.embedUrl,
            accessToken: response.data.embedToken,
            tokenType: models.TokenType.Embed,
            settings: {
              background: models.BackgroundType.Transparent,
              panes: {
                bookmarks: {
                  visible: false,
                },
                fields: {
                  expanded: false,
                },
                filters: {
                  expanded: true,
                  visible: true,
                },
                pageNavigation: {
                  visible: true,
                },
                selection: {
                  visible: true,
                },
                syncSlicers: {
                  visible: true,
                },
                visualizations: {
                  expanded: false,
                },
              },
              bars: {
                actionBar: {
                  visible: true,
                },
                statusBar: {
                  visible: false,
                },
              },
            },
          });
        })
        .catch((err) => {
          console.error("Failed to load Power BI report:", err);
        });
    },
    [reportType],
  );

  const handlePrint = async () => {
    if (window.report) {
      try {
        await window.report.print();
        console.log("Print dialog opened successfully");
      } catch (error) {
        console.error("Failed to print report:", error);
      }
    } else {
      console.error("Report not loaded yet");
    }
  };

  const buildFilterQueryString = async () => {
    const waitForReport = async () => {
      while (!window.report) {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      return window.report;
    };

    const report = await waitForReport();

    const reportFilters = await report.getFilters();

    const pages = await report.getPages();
    const activePage = pages.find((p) => p.isActive);

    const pageFilters = activePage ? await activePage.getFilters() : [];

    const allFilters = [...reportFilters, ...pageFilters];

    console.log("Raw Filters:", allFilters);

    // ---------- helpers ----------

    const isISODateTime = (value) => {
      if (typeof value !== "string") return false;
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
    };

    const formatDateTime = (d) => {
      const pad = (n) => n.toString().padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const endOfDay = (date) => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 0);
      return d;
    };

    const startOfWeek = (date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Monday start
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    // ---------- relativeTime ----------

    const getTimeUnitMs = (unitType) => {
      switch (unitType) {
        case 7:
          return 60 * 1000; // minutes
        case 8:
          return 60 * 60 * 1000; // hours
        default:
          console.warn("Unsupported timeUnitType:", unitType);
          return null;
      }
    };

    const buildRelativeTimeFilter = (f, table, column) => {
      const now = new Date();

      const unitMs = getTimeUnitMs(f.timeUnitType);
      if (!unitMs) return null;

      const durationMs = f.timeUnitsCount * unitMs;

      let start, end;

      switch (f.operator) {
        case 0: // last
          start = new Date(now.getTime() - durationMs);
          end = now;
          break;

        case 1: // this
          start = new Date(now);
          end = new Date(now);

          if (f.timeUnitType === 7) {
            start.setSeconds(0, 0);
            end.setSeconds(59, 999);
          }

          if (f.timeUnitType === 8) {
            start.setMinutes(0, 0, 0);
            end.setMinutes(59, 59, 999);
          }
          break;

        case 2: // next
          start = now;
          end = new Date(now.getTime() + durationMs);
          break;

        default:
          return null;
      }

      return `${table}/${column} ge datetime'${formatDateTime(start)}' and ${table}/${column} le datetime'${formatDateTime(end)}'`;
    };

    // ---------- relativeDate ----------

    const buildRelativeDateFilter = (f, table, column) => {
      const now = new Date();

      const count = f.timeUnitsCount;
      const unit = f.timeUnitType;
      const op = f.operator;
      const includeToday = f.includeToday;

      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);

      const todayEnd = endOfDay(now);
      const yesterdayEnd = endOfDay(new Date(todayStart.getTime() - 86400000));

      let start, end;

      switch (op) {
        // LAST
        case 0:
          end = includeToday ? todayEnd : yesterdayEnd;

          switch (unit) {
            case 0: // days
              start = new Date(todayStart);
              start.setDate(start.getDate() - count);
              break;

            case 1: // weeks
              start = new Date(todayStart);
              start.setDate(start.getDate() - count * 7);
              break;

            case 2: // calendar weeks
              start = startOfWeek(todayStart);
              start.setDate(start.getDate() - count * 7);
              break;

            case 3: // months
              start = new Date(todayStart);
              start.setMonth(start.getMonth() - count);
              break;

            case 4: // calendar months
              start = new Date(todayStart);
              start.setDate(1);
              start.setMonth(start.getMonth() - count);
              break;

            case 5: // years
              start = new Date(todayStart);
              start.setFullYear(start.getFullYear() - count);
              break;

            case 6: // calendar years
              start = new Date(todayStart);
              start.setMonth(0, 1);
              start.setFullYear(start.getFullYear() - count);
              break;

            default:
              return null;
          }
          break;

        // THIS
        case 1:
          switch (unit) {
            case 0:
              start = todayStart;
              end = todayEnd;
              break;

            case 1:
            case 2:
              start = startOfWeek(todayStart);
              end = new Date(start);
              end.setDate(start.getDate() + 6);
              end = endOfDay(end);
              break;

            case 3:
            case 4:
              start = new Date(
                todayStart.getFullYear(),
                todayStart.getMonth(),
                1,
              );
              end = new Date(
                todayStart.getFullYear(),
                todayStart.getMonth() + 1,
                0,
              );
              end = endOfDay(end);
              break;

            case 5:
            case 6:
              start = new Date(todayStart.getFullYear(), 0, 1);
              end = new Date(todayStart.getFullYear(), 11, 31);
              end = endOfDay(end);
              break;

            default:
              return null;
          }
          break;

        // NEXT
        case 2:
          start = todayStart;

          switch (unit) {
            case 0:
              end = new Date(todayStart);
              end.setDate(end.getDate() + count);
              end = endOfDay(end);
              break;

            case 1:
              end = new Date(todayStart);
              end.setDate(end.getDate() + count * 7);
              end = endOfDay(end);
              break;

            case 2:
              start = startOfWeek(todayStart);
              end = new Date(start);
              end.setDate(start.getDate() + count * 7);
              end = endOfDay(end);
              break;

            case 3:
              end = new Date(todayStart);
              end.setMonth(end.getMonth() + count);
              end = endOfDay(end);
              break;

            case 4:
              start = new Date(
                todayStart.getFullYear(),
                todayStart.getMonth(),
                1,
              );
              end = new Date(start);
              end.setMonth(start.getMonth() + count);
              end.setDate(0);
              end = endOfDay(end);
              break;

            case 5:
              end = new Date(todayStart);
              end.setFullYear(end.getFullYear() + count);
              end = endOfDay(end);
              break;

            case 6:
              start = new Date(todayStart.getFullYear(), 0, 1);
              end = new Date(start.getFullYear() + count, 11, 31);
              end = endOfDay(end);
              break;

            default:
              return null;
          }
          break;

        default:
          return null;
      }

      return `${table}/${column} ge datetime'${formatDateTime(start)}' and ${table}/${column} le datetime'${formatDateTime(end)}'`;
    };

    // ---------- main conversion ----------

    const convertFilter = (f) => {
      if (!f.target) return null;

      const table = f.target.table;
      const column = f.target.column;

      // relativeTime
      if (f.$schema?.includes("#relativeTime")) {
        return buildRelativeTimeFilter(f, table, column);
      }

      // relativeDate
      if (f.$schema?.includes("#relativeDate")) {
        return buildRelativeDateFilter(f, table, column);
      }

      // advanced filter with conditions
      if (
        f.$schema?.includes("#advanced") &&
        f.conditions &&
        Array.isArray(f.conditions)
      ) {
        const conditionStrings = f.conditions
          .map((condition) => {
            const value = condition.value;
            const operator = condition.operator;

            // Handle datetime values
            if (isISODateTime(value)) {
              const d = new Date(value);
              const formatted = formatDateTime(d);
              const datetimeValue = `datetime'${formatted}'`;

              switch (operator) {
                case "GreaterThan":
                  return `${table}/${column} gt ${datetimeValue}`;
                case "GreaterThanOrEqual":
                  return `${table}/${column} ge ${datetimeValue}`;
                case "LessThan":
                  return `${table}/${column} lt ${datetimeValue}`;
                case "LessThanOrEqual":
                  return `${table}/${column} le ${datetimeValue}`;
                case "Is":
                case "Equal":
                  return `${table}/${column} eq ${datetimeValue}`;
                case "IsNot":
                  return `${table}/${column} ne ${datetimeValue}`;
                default:
                  return null;
              }
            }

            // Handle numeric values
            if (typeof value === "number") {
              switch (operator) {
                case "GreaterThan":
                  return `${table}/${column} gt ${value}`;
                case "GreaterThanOrEqual":
                  return `${table}/${column} ge ${value}`;
                case "LessThan":
                  return `${table}/${column} lt ${value}`;
                case "LessThanOrEqual":
                  return `${table}/${column} le ${value}`;
                case "Is":
                case "Equal":
                  return `${table}/${column} eq ${value}`;
                case "IsNot":
                  return `${table}/${column} ne ${value}`;
                default:
                  return null;
              }
            }

            // Handle string values
            if (typeof value === "string" && !isISODateTime(value)) {
              const escapedValue = `'${value.toString().trim()}'`;
              switch (operator) {
                case "Is":
                case "Equal":
                  return `${table}/${column} eq ${escapedValue}`;
                case "IsNot":
                  return `${table}/${column} ne ${escapedValue}`;
                case "Contains":
                  return `contains(${table}/${column}, ${escapedValue})`;
                case "DoesNotContain":
                  return `not contains(${table}/${column}, ${escapedValue})`;
                case "StartsWith":
                  return `startswith(${table}/${column}, ${escapedValue})`;
                case "DoesNotStartWith":
                  return `not startswith(${table}/${column}, ${escapedValue})`;
                default:
                  return null;
              }
            }

            return null;
          })
          .filter(Boolean);

        if (conditionStrings.length === 0) return null;

        const logicalOp = f.logicalOperator === "Or" ? " or " : " and ";
        return conditionStrings.length === 1
          ? conditionStrings[0]
          : `(${conditionStrings.join(logicalOp)})`;
      }

      // datetime IN
      if (
        f.operator === "In" &&
        Array.isArray(f.values) &&
        f.values.length > 0 &&
        isISODateTime(f.values[0])
      ) {
        const formattedValues = f.values.map((v) => {
          const d = new Date(v);
          const formatted = formatDateTime(
            new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0),
          );
          return `datetime'${formatted}'`;
        });

        if (formattedValues.length === 1) {
          return `${table}/${column} eq ${formattedValues[0]}`;
        }

        return `${table}/${column} in (${formattedValues.join(",")})`;
      }

      // string IN
      if (f.operator === "In" && Array.isArray(f.values)) {
        const values = f.values
          .map((v) => `'${v.toString().trim()}'`)
          .join(",");
        return `${table}/${column} in (${values})`;
      }

      // equal
      if (f.operator === "Equal" && f.values?.length) {
        return `${table}/${column} eq '${f.values[0].toString().trim()}'`;
      }

      return null;
    };

    const filterStrings = allFilters.map(convertFilter).filter(Boolean);

    const combined = filterStrings.join(" and ");

    console.log("Combined Filter:", combined);

    return combined ? `&filter=${encodeURIComponent(combined)}` : "";
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      // Step 1: Build filter query string from current report filters
      console.log("Building filter query string...");
      let filterQueryString = "";

      try {
        filterQueryString = await buildFilterQueryString();
        console.log("Filter query string:", filterQueryString);
      } catch (filterError) {
        console.error("Error building filter query string:", filterError);
        // Continue with export even if filter building fails
      }

      // Step 2: Start the export with filters
      console.log("Starting export for report:", currentReport.id);
      console.log(
        "Full export URL will be:",
        `export-report?id=${currentReport.id}${filterQueryString}`,
      );
      const exportResponse = await exportPowerBIReport(
        currentReport.id,
        filterQueryString,
      );

      if (exportResponse.status === 200 && exportResponse.data) {
        const { reportId, exportId } = exportResponse.data;
        console.log("Export started. Export ID:", exportId);

        // Step 2: Poll for export status
        const pollInterval = 5000; // Poll every 5 seconds
        const maxAttempts = 36; // Maximum 3 minutes (36 * 5 seconds)
        let attempts = 0;

        const pollStatus = async () => {
          try {
            const statusResponse = await getPowerBIExportStatus(
              reportId,
              exportId,
            );

            if (statusResponse.Status === 200 && statusResponse.Data) {
              const { status, percentComplete } = statusResponse.Data;
              setExportProgress(percentComplete || 0);

              console.log(
                `Export status: ${status}, Progress: ${percentComplete}%`,
              );

              if (status === "Succeeded") {
                // Step 3: Download the file
                console.log("Export succeeded. Downloading file...");
                const downloadUrl = statusResponse.DownloadUrl;

                // Construct full URL with base API URL
                const fullDownloadUrl =
                  APP_BASE_URL_DEV + "benchmarkingmg" + downloadUrl;
                console.log("Full download URL:", fullDownloadUrl);

                try {
                  // Use NetworkService to download with authentication
                  const blobData = await NetworkService.getData(
                    fullDownloadUrl,
                    {},
                    "blob",
                  );

                  // Create blob URL and trigger download
                  const blob = new Blob([blobData], {
                    type: "application/pdf",
                  });
                  const blobUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = `${currentReport.label}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(blobUrl);

                  setIsExporting(false);
                  setExportProgress(100);
                  console.log("Download completed successfully");
                } catch (downloadError) {
                  console.error("Download failed:", downloadError);
                  setIsExporting(false);
                  setExportProgress(0);
                  alert("Failed to download report. Please try again.");
                }
                return;
              } else if (status === "Failed") {
                throw new Error("Export failed on server");
              } else if (attempts >= maxAttempts) {
                throw new Error(
                  "Export timeout - maximum polling attempts reached",
                );
              } else {
                // Continue polling
                attempts++;
                setTimeout(pollStatus, pollInterval);
              }
            } else {
              throw new Error("Invalid status response");
            }
          } catch (error) {
            console.error("Error polling export status:", error);
            setIsExporting(false);
            setExportProgress(0);
            alert("Failed to check export status. Please try again.");
          }
        };

        // Start polling
        pollStatus();
      } else {
        throw new Error("Failed to start export");
      }
    } catch (error) {
      console.error("Failed to export report:", error);
      setIsExporting(false);
      setExportProgress(0);
      alert("Failed to export report. Please try again.");
    }
  };

  function renderReport() {
    if (powerBIParams && powerBIParams.embedUrl) {
      return (
        <PowerBIEmbed
          embedConfig={powerBIParams}
          eventHandlers={
            new Map([
              [
                "loaded",
                function () {
                  console.log("Power BI Report loaded successfully");
                },
              ],
              [
                "rendered",
                function () {
                  console.log("Power BI Report rendered");
                },
              ],
              [
                "error",
                function (event) {
                  console.error("Power BI Error:", event.detail);
                },
              ],
            ])
          }
          cssClassName={"powerBIReportEmbed"}
          getEmbeddedComponent={(embeddedReport) => {
            window.report = embeddedReport;
          }}
        />
      );
    } else {
      return (
        <div
          className="powerBIReportEmbed"
          style={{ padding: "20px", textAlign: "center" }}
        >
          <p>
            Loading report... If this message persists, please contact support.
          </p>
        </div>
      );
    }
  }

  return (
    <article>
      <PageHeader
        h1={currentReport.label.toUpperCase()}
        actionButton={
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={!powerBIParams || isExporting}
              style={{
                height: "40px",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "uppercase",
              }}
            >
              {isExporting ? (
                <>
                  <CircularProgress
                    size={16}
                    style={{ marginRight: "8px", color: "white" }}
                  />
                  Exporting {exportProgress}%
                </>
              ) : (
                "Export Report"
              )}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrint}
              disabled={!powerBIParams}
              style={{
                height: "40px",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "uppercase",
                marginLeft: "16px",
              }}
            >
              Print Report
            </Button>
          </Box>
        }
      />
      <Container
        maxWidth={false}
        className="container-align-left breadcrumb-wrapper"
      >
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          className="breadcrumb-container"
        >
          <Link color="inherit" href="#">
            BI Report
          </Link>
          <Link color="inherit" href="#">
            {currentReport.label}
          </Link>
        </Breadcrumbs>
      </Container>

      <Container
        maxWidth={false}
        className="container-align-left"
        style={{ height: "calc(100vh - 200px)", minHeight: "600px" }}
      >
        {renderReport()}
      </Container>
    </article>
  );
};

export default PowerBi;
